const replacedBy = new Map<string, string>();
const inFlight = new Map<string, Promise<string | null>>();
const failed = new Set<string>();
const sessionExpiredListeners = new Set<() => void>();

export function currentIdentityToken(snapshot: string): string {
  let token = snapshot;
  let next = replacedBy.get(token);
  while (next !== undefined) {
    token = next;
    next = replacedBy.get(token);
  }
  return token;
}

export function onIdentitySessionExpired(listener: () => void): () => void {
  sessionExpiredListeners.add(listener);
  return () => {
    sessionExpiredListeners.delete(listener);
  };
}

function emitSessionExpired(): void {
  for (const listener of sessionExpiredListeners) {
    try {
      listener();
    } catch (error) {
      // Isolated per listener so one broken subscriber cannot starve the others.
      // eslint-disable-next-line no-console
      console.error("[IdentityTokenRefresher] session expired listener threw", { error });
    }
  }
}

/** For a token the server rejected after it was issued by a refresh; it is never refreshed again. */
export function markIdentityTokenFailed(token: string): void {
  if (failed.has(token)) return;
  failed.add(token);
  emitSessionExpired();
}

async function runRefresh(stale: string, refresh: () => Promise<string | null>): Promise<string | null> {
  let fresh: string | null;
  try {
    fresh = await refresh();
  } catch (error) {
    // Logger is silent in browsers; console reaches Sentry breadcrumbs.
    // eslint-disable-next-line no-console
    console.warn("[IdentityTokenRefresher] refresh threw", { error });
    fresh = null;
  }
  // A token already retired is no replacement, and recording it would loop the replacement chain.
  if (fresh == null || fresh === stale || replacedBy.has(fresh) || failed.has(fresh)) {
    failed.add(stale);
    emitSessionExpired();
    return null;
  }
  replacedBy.set(stale, fresh);
  return fresh;
}

/** One shared refresh per stale token; a failure is final for that token and emits session expired once. */
export function refreshStaleIdentityToken(
  stale: string,
  refresh: () => Promise<string | null>,
): Promise<string | null> {
  if (replacedBy.has(stale)) return Promise.resolve(currentIdentityToken(stale));
  if (failed.has(stale)) return Promise.resolve(null);

  const pending = inFlight.get(stale);
  if (pending) return pending;

  const started = runRefresh(stale, refresh).finally(() => inFlight.delete(stale));
  inFlight.set(stale, started);
  return started;
}

export function resetIdentityTokenRefresherForTests(): void {
  replacedBy.clear();
  inFlight.clear();
  failed.clear();
  sessionExpiredListeners.clear();
}
